import React from 'react';

import {useSnackbar} from 'notistack';
import {CallMade} from '@material-ui/icons';
import {Typography} from '@material-ui/core';

import {EfeLinkButton, EfeModal, EfeOtp} from 'components';
import {useAlert, useSpinner} from 'hooks';
import Security from 'services/security/Security';
import {EfeResponse, ValidateTokenResponse, ResetPasswordValues} from 'commons/models';
import PasswordForm from './password-form/PasswordForm';
import {Messages, SnackbarVariants} from 'commons/constants';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {BottomContent, MainContent} from 'layouts/commons';
import {MailAuthResponse, OtpValidation} from 'commons/types/Types';
import {ClientService} from 'services';

function ResetPassword() {
    const {enqueueSnackbar} = useSnackbar();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [showLogin, setShowLogin] = React.useState<boolean>(false);
    const [message, setMessage] = React.useState<string>(Messages.TOKEN_RESET_PASS_ERROR);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [valuesToSubmit, setValuesToSubmit] = React.useState<ResetPasswordValues>();
    const [token, setToken] = React.useState<boolean>(false);
    const [open, setOpen] = React.useState<boolean>(false);
    const [idKycPassword, setKycIdPassword] = React.useState<number>(0);

    const query = new URLSearchParams(window.location.search);

    function invalidToken() {
        setShowLogin(true);
        setMessage(Messages.TOKEN_RESET_PASS_ERROR);
        enqueueSnackbar(Messages.TOKEN_RESET_PASS_ERROR, {
            variant: SnackbarVariants.ERROR,
        });
        setIsLoading(false);
    }

    async function verifyToken(token: string) {
        try {
            const response: EfeResponse<ValidateTokenResponse> =
                await Security.validateRecoveryToken(token);
            if (
                !response ||
                !response.data ||
                !response.data.remainingTime ||
                response.data.remainingTime <= 0
            ) {
                enqueueSnackbar(Messages.TOKEN_RESET_PASS_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
                setShowLogin(true);
            } else {
                setKycIdPassword(response.data?.kycId || 0);
            }
        } catch (error: any) {
            setShowLogin(true);
            showAlertError(error, Messages.TOKEN_RESET_PASS_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    React.useEffect(() => {
        const token: string | null = query.get('token');
        if (token) {
            verifyToken(token);
        } else {
            invalidToken();
        }
    }, []);

    async function handleOtp(values: string) {
        setIsLoading(true);
        try {
            const response: EfeResponse<OtpValidation> =
                await ClientService.validateMailAuthentication(idKycPassword, values);
            if (response && response.data && response.data.authenticated) {
                setToken(!open);
                setOpen(!open);
                if(valuesToSubmit) {
                    sendSubmitValues(valuesToSubmit);
                }
                enqueueSnackbar(Messages.OTP_AUTHORIZATION_Ok, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.OTP_UNAUTHORIZED, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.OTP_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    async function getOtp() {
        setIsLoading(true);
        try {
            const response: EfeResponse<MailAuthResponse> =
                await ClientService.getMailAuthentication(idKycPassword);
            if (response && response.data) {
                setOpen(!open);
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.OTP_AUTHORIZATION_ERROR, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.OTP_AUTHORIZATION_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    async function sendSubmitValues(values: ResetPasswordValues) {
        setIsLoading(true);
        const token: string | null = query.get('token');
        if (token) {
            const request = {...values, token: token};
            request.token = token;

            try {
                const response = await Security.resetPassword(request, token);
                if (response && response.data) {
                    enqueueSnackbar(response.data.message || Messages.PASSWORD_RESET_OK, {
                        variant: SnackbarVariants.SUCCESS,
                    });
                    setMessage(Messages.PASSWORD_RESET_OK);
                    setShowLogin(true);
                } else {
                    enqueueSnackbar(Messages.PASSWORD_RESET_ERROR, {
                        variant: SnackbarVariants.ERROR,
                    });
                }
            } catch (error) {
                showAlertError(error, Messages.PASSWORD_RESET_ERROR);
            } finally {
                setIsLoading(false);
            }
        } else {
            invalidToken();
        }
    }
    
    async function submitHandler(values: ResetPasswordValues) {
        setValuesToSubmit(values);
        getOtp();
    }

    const ResultInfo = (
        <>
            <MainContent>
                <Typography variant='body1' color='textPrimary'>
                    {message}
                </Typography>
            </MainContent>
            <BottomContent>
                <EfeLinkButton primary path={'/admin/login'} endIcon={<CallMade />}>
                    {'Login'}
                </EfeLinkButton>
            </BottomContent>
        </>
    );

    return (
        <>
            {isLoading && useSpinner(isLoading)}
            {open && (
                    <EfeModal
                        open={open}
                        title={'Doble factor de autenticación'}
                        sizeOverride={30}
                        reactElement={
                            <EfeOtp
                                title='Introduce el código que acabamos de enviar a tu correo electrónico para poder guardar los cambios'
                                length={6}
                                isPassword={false}
                                handleClick={handleOtp}
                            />
                        }
                        handleAction={() => setOpen(!open)}
                    />
                )}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            {showLogin ? (
                ResultInfo
            ) : (
                <PasswordForm submitHandler={submitHandler} isLoading={isLoading} />
            )}
        </>
    );
}

export default ResetPassword;
